import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Container,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery,
  LinearProgress
} from "@mui/material";
import {
  Person,
  Email,
  Phone,
  UploadFile,
  Description,
  LocationOn,
  HelpOutline,
  NavigateNext,
  NavigateBefore,
  CheckCircle,
  Error as ErrorIcon,
  AccessTime
} from "@mui/icons-material";
import axios from "../Services/axiosConfig";

interface FormData {
  jobTitle: string;
  jobDescription: string;
  screeningQuestions: string[];
  deadline: string;
}

interface CandidateApplicationFormProps {
  formId: string;
  onSuccess: () => void;
}

// File size limit (5MB)
const MAX_FILE_SIZE = 5 * 1024 * 1024;
// Allowed file types
const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document" // .docx
];

const CandidateApplicationForm: React.FC<CandidateApplicationFormProps> = ({
  formId,
  onSuccess,
}) => {
  // Theme for responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Form data and fields
  const [formData, setFormData] = useState<FormData | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState("");
  const [location, setLocation] = useState("");
  const [answers, setAnswers] = useState<string[]>([]);
  const [cv, setCv] = useState<File | null>(null);
  
  // Form validation
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [fileError, setFileError] = useState<string>("");
  const [fieldsTouched, setFieldsTouched] = useState<{[key: string]: boolean}>({});
  
  // UI state
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "info" | "warning">("info");
  const [showThankYou, setShowThankYou] = useState(false);
  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<string>("");
  
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Steps for the stepper
  const steps = ["Personal Information", "Screening Questions", "Upload CV & Submit"];

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const response = await axios.get(`jobs/forms/${formId}`);
        const deadline = new Date(response.data.deadline);
        const currentDate = new Date();

        setFormData(response.data);
        setAnswers(new Array(response.data.screeningQuestions.length).fill(""));

        if (currentDate > deadline) {
          setDeadlinePassed(true);
        } else {
          // Calculate and display time remaining until deadline
          updateTimeRemaining(deadline);
          const timer = setInterval(() => updateTimeRemaining(deadline), 60000); // Update every minute
          return () => clearInterval(timer);
        }
      } catch (error) {
        console.error("Error fetching form data:", error);
        setSnackbarMessage("Failed to load the application form. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFormData();
  }, [formId]);

  // Function to update time remaining display
  const updateTimeRemaining = (deadline: Date) => {
    const now = new Date();
    const diff = deadline.getTime() - now.getTime();
    
    if (diff <= 0) {
      setDeadlinePassed(true);
      setTimeRemaining("");
      return;
    }
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      setTimeRemaining(`${days} day${days !== 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''} remaining`);
    } else if (hours > 0) {
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setTimeRemaining(`${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''} remaining`);
    } else {
      const minutes = Math.floor(diff / (1000 * 60));
      setTimeRemaining(`${minutes} minute${minutes !== 1 ? 's' : ''} remaining`);
    }
  };

  // Field validation
  const validateField = (field: string, value: any): string => {
    switch (field) {
      case 'name':
        return value.trim() === "" ? "Name is required" : "";
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value) ? "Please enter a valid email address" : "";
      case 'phone':
        const phoneRegex = /^\+?[0-9\s-()]{8,20}$/;
        return !phoneRegex.test(value) ? "Please enter a valid phone number" : "";
      case 'gender':
        return value === "" ? "Please select your gender" : "";
      case 'location':
        return value.trim() === "" ? "Location is required" : "";
      case 'cv':
        if (!value) return "Please upload your CV";
        if (!ALLOWED_FILE_TYPES.includes(value.type)) {
          return "Only PDF and DOCX files are accepted";
        }
        if (value.size > MAX_FILE_SIZE) {
          return "File size should not exceed 5MB";
        }
        return "";
      default:
        if (field.startsWith('answer-')) {
          const index = parseInt(field.split('-')[1]);
          return !value || value.trim() === "" 
            ? "This question requires an answer" 
            : "";
        }
        return "";
    }
  };

  // Handle field change and validation
  const handleFieldChange = (field: string, value: any) => {
    // Update touched state
    setFieldsTouched(prev => ({ ...prev, [field]: true }));
    
    // Validate and update errors
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
    
    // Update field value
    switch (field) {
      case 'name': setName(value); break;
      case 'email': setEmail(value); break;
      case 'phone': setPhone(value); break;
      case 'gender': setGender(value); break;
      case 'location': setLocation(value); break;
      case 'cv': 
        setCv(value);
        setFileError(error);
        break;
      default:
        if (field.startsWith('answer-')) {
          const index = parseInt(field.split('-')[1]);
          const newAnswers = [...answers];
          newAnswers[index] = value;
          setAnswers(newAnswers);
        }
    }
  };

  // File upload handler with validation
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setFileError("Only PDF and DOCX files are accepted");
        setCv(null);
        return;
      }
      
      if (file.size > MAX_FILE_SIZE) {
        setFileError("File size should not exceed 5MB");
        setCv(null);
        return;
      }
      
      setFileError("");
      setCv(file);
    } else {
      setCv(null);
    }
    
    setFieldsTouched(prev => ({ ...prev, cv: true }));
  };

  // Validation for current step
  const validateStep = (step: number): boolean => {
    let isValid = true;
    const newErrors: {[key: string]: string} = {};
    
    if (step === 0) {
      // Personal Information validation
      const fields = ['name', 'email', 'phone', 'gender', 'location'];
      const values = [name, email, phone, gender, location];
      
      fields.forEach((field, index) => {
        const error = validateField(field, values[index]);
        if (error) {
          isValid = false;
          newErrors[field] = error;
        }
      });
    } 
    else if (step === 1 && formData) {
      // Screening Questions validation
      formData.screeningQuestions.forEach((_, index) => {
        const error = validateField(`answer-${index}`, answers[index]);
        if (error) {
          isValid = false;
          newErrors[`answer-${index}`] = error;
        }
      });
    }
    else if (step === 2) {
      // CV validation
      const error = validateField('cv', cv);
      if (error) {
        isValid = false;
        setFileError(error);
      }
    }
    
    setErrors(prev => ({...prev, ...newErrors}));
    return isValid;
  };

  // Step navigation
  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
      window.scrollTo(0, 0);
    } else {
      // Mark all fields in current step as touched
      if (activeStep === 0) {
        setFieldsTouched({
          ...fieldsTouched,
          name: true,
          email: true,
          phone: true,
          gender: true,
          location: true
        });
      } else if (activeStep === 1) {
        const newTouched = {...fieldsTouched};
        formData?.screeningQuestions.forEach((_, index) => {
          newTouched[`answer-${index}`] = true;
        });
        setFieldsTouched(newTouched);
      } else if (activeStep === 2) {
        setFieldsTouched({...fieldsTouched, cv: true});
      }
      
      setSnackbarMessage("Please correct the errors before proceeding");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    window.scrollTo(0, 0);
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Final validation of all steps
    const personalInfoValid = validateStep(0);
    const questionsValid = validateStep(1);
    const cvValid = validateStep(2);
    
    if (!personalInfoValid || !questionsValid || !cvValid) {
      setSnackbarMessage("Please fill all required fields correctly");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulated progress for better UX
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + Math.random() * 20;
        return newProgress >= 90 ? 90 : newProgress;
      });
    }, 500);

    const applicationData = new FormData();
    applicationData.append("name", name);
    applicationData.append("email", email);
    applicationData.append("phone", phone);
    applicationData.append("gender", gender);
    applicationData.append("location", location);
    applicationData.append("answers", JSON.stringify(answers));

    if (cv) {
      applicationData.append("cv", cv);
    }

    try {
      await axios.post(`jobs/forms/${formId}/submit`, applicationData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProgress(100);
      setSnackbarMessage("Application submitted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setShowThankYou(true);
      onSuccess();
    } catch (error: any) {
      console.error("Error submitting application:", error);
    
      if (error.response?.status === 409) {
        setAlreadyApplied(true);
        setSnackbarMessage("You've already submitted an application for this job.");
      } else {
        setSnackbarMessage("Failed to submit application. Please try again.");
      }
      
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      clearInterval(progressInterval);
      setIsSubmitting(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Loading state
  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <CircularProgress size={60} thickness={5} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading application form...
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Form not found
  if (!formData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: "center", borderRadius: 2, bgcolor: "#fff9f9" }}>
          <ErrorIcon color="error" sx={{ fontSize: 60 }} />
          <Typography variant="h5" color="error" gutterBottom mt={2}>
            Form not found
          </Typography>
          <Typography variant="body1">
            Sorry, we couldn't find the application form you're looking for. It may have been removed or the link is incorrect.
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Thank you page, deadline passed or already applied
  if (showThankYou || deadlinePassed || alreadyApplied) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            textAlign: "center", 
            borderRadius: 2,
            bgcolor: deadlinePassed ? "#fff9f9" : alreadyApplied ? "#f9f9ff" : "#f5fff9"
          }}
        >
          {deadlinePassed ? (
            <AccessTime sx={{ fontSize: 60, color: "#d32f2f" }} />
          ) : alreadyApplied ? (
            <ErrorIcon sx={{ fontSize: 60, color: "#3f51b5" }} />
          ) : (
            <CheckCircle sx={{ fontSize: 60, color: "#4caf50" }} />
          )}
          
          <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
            {deadlinePassed ? (
              "Application Closed"
            ) : alreadyApplied ? (
              "Already Applied"
            ) : (
              "Thank You!"
            )}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            {deadlinePassed
              ? "The application deadline has passed. Applications are no longer being accepted for this position."
              : alreadyApplied
                ? "Our records show you've already submitted an application for this job. Thank you for your interest!"
                : "Your application has been submitted successfully. We'll review your application and contact you soon."}
          </Typography>
          {/* 
          {deadlinePassed && (
            <Button 
              variant="outlined" 
              color="primary" 
              href="/jobs" 
              sx={{ mt: 2 }}
            >
              View Other Opportunities
            </Button>
          )} */}
        </Paper>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 5 }}>
      <Paper elevation={6} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3 }}>
        {/* Job header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography 
            variant="h4" 
            gutterBottom 
            sx={{ 
              fontWeight: "bold", 
              color: "primary.main",
              fontSize: { xs: '1.75rem', sm: '2.25rem' }
            }}
          >
            {formData.jobTitle}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
            <Chip 
              icon={<AccessTime fontSize="small" />}
              label={`Deadline: ${new Date(formData.deadline).toLocaleDateString()}`}
              color="primary"
              variant="outlined"
              sx={{ mr: 1 }}
            />
            
            {timeRemaining && (
              <Chip 
                label={timeRemaining}
                color="secondary"
                size="small"
              />
            )}
          </Box>
          
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 3, 
              fontStyle: "italic",
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6
            }}
          >
            {formData.jobDescription}
          </Typography>
        </Box>
        
        {/* Stepper */}
        <Stepper 
          activeStep={activeStep} 
          alternativeLabel={!isMobile}
          orientation={isMobile ? "vertical" : "horizontal"}
          sx={{ mb: 4 }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <form ref={formRef} onSubmit={handleSubmit}>
          {/* Step 1: Personal Information */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
                Personal Information
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Full Name"
                    fullWidth
                    value={name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    error={fieldsTouched.name && !!errors.name}
                    helperText={fieldsTouched.name && errors.name}
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Person /></InputAdornment>,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    fullWidth
                    type="email"
                    value={email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    error={fieldsTouched.email && !!errors.email}
                    helperText={fieldsTouched.email && errors.email}
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Email /></InputAdornment>,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone Number"
                    fullWidth
                    value={phone}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                    error={fieldsTouched.phone && !!errors.phone}
                    helperText={fieldsTouched.phone && errors.phone}
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Phone /></InputAdornment>,
                    }}
                    placeholder="+1 (555) 123-4567"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl 
                    fullWidth 
                    error={fieldsTouched.gender && !!errors.gender}
                  >
                    <InputLabel>Gender</InputLabel>
                    <Select
                      label="Gender"
                      value={gender}
                      onChange={(e) => handleFieldChange('gender', e.target.value)}
                      required
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                    {fieldsTouched.gender && errors.gender && (
                      <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.gender}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Location"
                    fullWidth
                    value={location}
                    onChange={(e) => handleFieldChange('location', e.target.value)}
                    error={fieldsTouched.location && !!errors.location}
                    helperText={fieldsTouched.location && errors.location}
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><LocationOn /></InputAdornment>,
                    }}
                    placeholder="City, Country"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
          
          {/* Step 2: Screening Questions */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
                Screening Questions
              </Typography>
              
              {formData.screeningQuestions.map((question, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {index + 1}. {question}
                    </Typography>
                    <Tooltip title="Maximum 500 characters">
                      <IconButton size="small" sx={{ ml: 1, mt: -0.5 }}>
                        <HelpOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  <TextField
                    fullWidth
                    value={answers[index]}
                    onChange={(e) => handleFieldChange(`answer-${index}`, e.target.value)}
                    error={fieldsTouched[`answer-${index}`] && !!errors[`answer-${index}`]}
                    helperText={
                      fieldsTouched[`answer-${index}`] && errors[`answer-${index}`] ? 
                        errors[`answer-${index}`] : 
                        `${answers[index].length}/500 characters used`
                    }
                    required
                    multiline
                    rows={3}
                    inputProps={{ maxLength: 500 }}
                    placeholder="Type your answer here..."
                  />
                </Box>
              ))}
            </Box>
          )}
          
          {/* Step 3: CV Upload & Submit */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
                Upload Your CV
              </Typography>
              
              <Box 
                sx={{
                  border: '2px dashed',
                  borderColor: fileError ? 'error.main' : cv ? 'success.main' : 'grey.300',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  mb: 3,
                  bgcolor: fileError ? 'error.lightest' : cv ? 'success.lightest' : 'grey.50',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: fileError ? 'error.main' : 'primary.main',
                    bgcolor: fileError ? 'error.lightest' : 'primary.lightest'
                  }
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx"
                  hidden
                  onChange={handleFileChange}
                />
                
                <UploadFile sx={{ fontSize: 48, color: fileError ? 'error.main' : cv ? 'success.main' : 'text.secondary' }} />
                
                <Typography variant="h6" sx={{ mt: 2 }}>
                  {cv ? "File Selected" : "Upload CV"}
                </Typography>
                
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {cv ? cv.name : "Click or drag to upload your CV (PDF or DOCX only, max 5MB)"}
                </Typography>
                
                {fileError && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {fileError}
                  </Typography>
                )}
                
                {cv && !fileError && (
                  <Chip 
                    label="File ready to upload" 
                    color="success" 
                    icon={<CheckCircle />} 
                    sx={{ mt: 2 }} 
                  />
                )}
              </Box>
              
              {isSubmitting && (
                <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
                    {progress < 100 ? "Submitting your application..." : "Complete!"}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          
          {/* Navigation buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              startIcon={<NavigateBefore />}
              disabled={activeStep === 0 || isSubmitting}
            >
              Back
            </Button>
            
            {activeStep === steps.length - 1 ? (
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                sx={{ minWidth: 120 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : "Submit"}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                endIcon={<NavigateNext />}
                sx={{ minWidth: 120 }}
              >
                Next
              </Button>
            )}
          </Box>
        </form>
      </Paper>

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CandidateApplicationForm;